// 정산 검증 화면 (v0 골격).
//
// 문서 요구사항: 입출금·세금계산서 대조, 미반환 확인, 금액 불일치, 수동 수정 내역을
// 한 곳에서 확인. 실 세부 요구사항(대조 기준·데이터 소스·판정 룰)이 미확정이라
// 현 시점에는 실행 가능한 하위 항목만 개별 카드로 노출.
// (Phase 2에서 각 카드의 실제 로직 개발 예정)

import { useEffect, useState } from 'react';
import { AlertTriangle, ClipboardCheck, DollarSign, Landmark, ScrollText } from 'lucide-react';
import { api, fmt } from '../api';

type UnpaidRow = {
  mcht_id: string;
  merchant_name: string | null;
  prev_shortfall: number;
  remaining_amount: number;
  litigation_status?: string | null;
  litigation_has_cost?: number;
  contract_ended_at?: string | null;
};

export default function SettlementVerify() {
  const [unpaid, setUnpaid] = useState<UnpaidRow[]>([]);
  const [unpaidLoading, setUnpaidLoading] = useState(true);

  const [adjTotals, setAdjTotals] = useState<{ merchants: number; net_amount: number } | null>(null);
  const [adjLoading, setAdjLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setUnpaidLoading(true);
      try {
        // 무매출 알림 데이터에서 미반환 (잔여액 > 0) 후보를 재활용.
        // 별도 endpoint를 만들 필요가 없어 재사용.
        const kst = new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 10);
        const r = await api<{ data: UnpaidRow[] }>('GET', `/settlements/alerts/dormant?asof=${kst}&days=90`);
        const arr = (r.data ?? []).filter(x => (x.remaining_amount ?? 0) > 0);
        arr.sort((a, b) => (b.remaining_amount ?? 0) - (a.remaining_amount ?? 0));
        setUnpaid(arr);
      } finally { setUnpaidLoading(false); }
    })();
    (async () => {
      setAdjLoading(true);
      try {
        // 지금은 개별 가맹점 화면에만 조정 이력이 있어 전체 요약 endpoint가 없다.
        // 요구사항 명세 후 백엔드에 SUM 집계 endpoint를 추가하기 전까지는 안내만.
        setAdjTotals(null);
      } finally { setAdjLoading(false); }
    })();
  }, []);

  return (
    <div>
      <h2>정산 검증</h2>
      <p style={{ color: 'var(--text-muted)', fontSize: 13, marginTop: -6 }}>
        입출금·세금계산서 대조, 미반환 확인, 금액 불일치, 수동 조정 내역을 한 곳에서 점검하는 화면입니다.
        각 항목의 실제 판정 로직은 요구사항 확정 후 순차 도입 예정입니다.
      </p>

      <div className="card" style={{ marginTop: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
          <AlertTriangle size={18} style={{ color: 'var(--warn)' }} />
          <h3 style={{ margin: 0, fontSize: 15 }}>미반환 잔여액 상위 (전체 활성 가맹점)</h3>
        </div>
        <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: '0 0 10px' }}>
          기준일 최근 90일 무매출 조회 결과 중 잔여액 &gt; 0인 가맹점입니다. 소송·계약 상태와 함께 확인하세요.
        </p>
        {unpaidLoading ? (
          <div className="loading">조회 중…</div>
        ) : unpaid.length === 0 ? (
          <div className="empty-state"><p>미반환 잔여액이 있는 가맹점이 없습니다.</p></div>
        ) : (
          <div className="table-wrap" style={{ maxHeight: '45vh' }}>
            <table>
              <thead><tr>
                <th className="nowrap">MID</th>
                <th>가맹점명</th>
                <th className="num nowrap">전일미차감</th>
                <th className="num nowrap">잔여액</th>
                <th className="nowrap">소송·계약</th>
              </tr></thead>
              <tbody>
                {unpaid.slice(0, 100).map(r => (
                  <tr key={r.mcht_id}>
                    <td className="nowrap cell-mono" style={{ color: 'var(--muted)' }}>{r.mcht_id}</td>
                    <td style={{ fontWeight: 600 }}>{r.merchant_name ?? '-'}</td>
                    <td className="num nowrap">{fmt.won(r.prev_shortfall ?? 0)}원</td>
                    <td className="num nowrap" style={{ color: '#c2410c', fontWeight: 700 }}>{fmt.won(r.remaining_amount)}원</td>
                    <td className="nowrap" style={{ fontSize: 11 }}>
                      {r.litigation_status === 'in_progress' && <span style={{ color: '#991b1b', fontWeight: 700 }}>소송 진행 중 </span>}
                      {(r.litigation_has_cost ?? 0) === 1 && <span style={{ color: '#854d0e', fontWeight: 600 }}>비용 </span>}
                      {r.contract_ended_at && <span style={{ color: '#3730a3', fontWeight: 600 }}>계약 종료</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 12, marginTop: 12 }}>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <Landmark size={16} style={{ color: 'var(--brand-700)' }} />
            <b>입출금 대조</b>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            가맹점 이체 이력과 은행 입출금 내역 대조. <b>요구사항 정의 필요</b>: 은행 데이터 연동 방식(수동 업로드 vs API), 매칭 기준(금액·계좌·일시), 오차 허용 범위.
          </div>
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <ScrollText size={16} style={{ color: 'var(--brand-700)' }} />
            <b>세금계산서 대조</b>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            발행/수취 세금계산서와 정산 실적 대조. <b>요구사항 정의 필요</b>: 세금계산서 데이터 소스, 대조 주기, 미발행/오류 판정 룰.
          </div>
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <DollarSign size={16} style={{ color: 'var(--brand-700)' }} />
            <b>금액 불일치 감시</b>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            결제 수신내역 매출 합계 vs 정산 반영 매출 vs 실지급액의 3자 대조. 현재는 결제 수신내역 화면의 "정산 반영 확인 필요" 뱃지가 유사 기능을 담당.
          </div>
        </div>
        <div className="card" style={{ padding: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <ClipboardCheck size={16} style={{ color: 'var(--brand-700)' }} />
            <b>수동 조정 내역</b>
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', lineHeight: 1.6 }}>
            잔여액 조정·반환 조정·통신비 자동 반영 이력의 전체 요약. 현재는 가맹점 수정 화면에서 개별 확인 가능.
            {adjLoading ? ' (요약 로딩…)' : adjTotals ? `총 ${adjTotals.merchants}개 가맹점, 순합 ${fmt.won(adjTotals.net_amount)}원` : ' (전체 요약 endpoint 미개발)'}
          </div>
        </div>
      </div>
    </div>
  );
}
